import { UISpecCategory, AirflowUISpecCategory } from '@common/enums';
import { AirflowSpecLanguage, SpecLanguage } from '@common/types';
import { PlusIcon } from '@prophecy/icons';
import { theme } from '@prophecy/theme';
import { Stack, Select, Button } from '@prophecy/ui-v3';
import { useCallback, useId } from 'react';
import { useSearchParams } from 'react-router-dom';
import styled from 'styled-components';

import { useGlobalVariables } from '../../../../context/globalConfig/securedConfigHook';
import { useGemsandLanguages } from './hooks';
import { GemSectionCategory, GemToolbarProps } from './types';

const StyledSelect = styled(Select)`
  width: 130px;
`;

export function GemListToolbar(props: GemToolbarProps) {
  const { section: gemSection, onAddGem } = props;
  const isAirflow = gemSection === GemSectionCategory.Airflow;

  const globalConf = useGlobalVariables();
  let [, setSearchParams] = useSearchParams();
  const { gemTypes, languageOpts, language, gemType } = useGemsandLanguages(isAirflow);
  const gemCategoryLabelId = useId();
  const languageLabelId = useId();

  const updateSearch = useCallback(
    ({
      language,
      gemType
    }: {
      language: SpecLanguage | AirflowSpecLanguage;
      gemType: AirflowUISpecCategory | UISpecCategory;
    }) => {
      setSearchParams(`lang=${language}&type=${gemType}`);
    },
    [setSearchParams]
  );

  const legacyAirflowCategories = [AirflowUISpecCategory.Operator];

  // show legacy categories like operator and sensor only when at-least one gem with that category exists
  const _gemTypes = isAirflow
    ? gemTypes.filter((gemType) => {
        const isLegacyCategory = legacyAirflowCategories.find((category) => category === gemType.label);
        if (isLegacyCategory && !props.gemCategories[gemType.label]) return false;
        else return true;
      })
    : gemTypes;

  return (
    <>
      <Stack direction='horizontal' gap={theme.spaces.x12} alignY='center'>
        <label id={gemCategoryLabelId}>Gem Category:</label>
        <StyledSelect
          options={_gemTypes}
          onChange={(gemType: UISpecCategory | AirflowUISpecCategory) => updateSearch({ language, gemType })}
          disabled={!globalConf.isPythonLanguageEnabled}
          value={gemType}
          data-test-id='gem-list-category-select'
          aria-labelledby={gemCategoryLabelId}
        />
        <label id={languageLabelId}>Language:</label>
        <StyledSelect
          options={languageOpts}
          onChange={(language: SpecLanguage) => updateSearch({ language, gemType })}
          disabled={!globalConf.isPythonLanguageEnabled}
          value={language}
          data-test-id='gem-list-language-select'
          aria-labelledby={languageLabelId}
        />
        <Button
          variant='secondary'
          icon={<PlusIcon type='default' />}
          onClick={onAddGem}
          data-test-id='gem-list-add-gem-button'>
          Add Gem
        </Button>
      </Stack>
    </>
  );
}
